import { apiRequest } from '@services/apiClient';
import multimediaEndpoints from '@services/multimedia/multimediaEndpoints';

function shouldConvertImageToWebp(file) {
  const mimeType = String(file?.type || '').toLowerCase();
  return (
    mimeType.startsWith('image/') &&
    mimeType !== 'image/webp' &&
    mimeType !== 'image/gif' &&
    mimeType !== 'image/svg+xml'
  );
}

function renameFileExtension(fileName = '', extension = '.webp') {
  const safeName = String(fileName || 'upload').trim() || 'upload';
  const normalizedExtension = String(extension || '.webp');
  return safeName.replace(/\.[^/.]+$/, '') + normalizedExtension;
}

function convertImageFileToWebp(file) {
  if (!shouldConvertImageToWebp(file)) {
    return Promise.resolve(file);
  }
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || image.width || 0;
        canvas.height = image.naturalHeight || image.height || 0;
        if (!canvas.width || !canvas.height) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }
        const context = canvas.getContext('2d');
        if (!context) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
              resolve(file);
              return;
            }
            resolve(
              new File([blob], renameFileExtension(file.name, '.webp'), {
                type: 'image/webp',
                lastModified: Date.now(),
              }),
            );
          },
          'image/webp',
          0.9,
        );
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    image.src = objectUrl;
  });
}

async function normalizeFilesForUpload(files = []) {
  const safeFiles = Array.isArray(files) ? files : [];
  return Promise.all(safeFiles.map((file) => convertImageFileToWebp(file)));
}

async function buildMultimediaFormData(payload = {}) {
  const formData = new FormData();
  if (Array.isArray(payload.files) && payload.files.length > 1) {
    const normalizedFiles = await normalizeFilesForUpload(payload.files);
    normalizedFiles.forEach((file) => formData.append('files', file));
  } else if (Array.isArray(payload.files) && payload.files.length === 1) {
    const [normalizedFile] = await normalizeFilesForUpload(payload.files);
    if (normalizedFile) formData.append('file', normalizedFile);
  } else if (payload.file) {
    const [normalizedFile] = await normalizeFilesForUpload([payload.file]);
    if (normalizedFile) formData.append('file', normalizedFile);
  }
  return formData;
}

export async function createMultimediaRequest(payload) {
  return apiRequest(multimediaEndpoints.base, {
    method: 'POST',
    body: await buildMultimediaFormData(payload),
    headers: {}
  });
}

export function importMultimediaFromUrlRequest(url = '') {
  return apiRequest(multimediaEndpoints.importUrl, {
    method: 'POST',
    body: {
      url: String(url || '').trim()
    }
  });
}

export function listMultimediaRequest(params = {}) {
  const searchParams = new URLSearchParams();
  if (params.isActive !== undefined && params.isActive !== null && params.isActive !== '') {
    searchParams.set('isActive', String(params.isActive));
  }
  if (params.mediaType) searchParams.set('mediaType', params.mediaType);
  const query = searchParams.toString();
  const path = query ? `${multimediaEndpoints.base}?${query}` : multimediaEndpoints.base;
  return apiRequest(path);
}

export function getMultimediaByIdRequest(id) {
  return apiRequest(multimediaEndpoints.byId(id));
}

export async function updateMultimediaRequest(id, payload) {
  return apiRequest(multimediaEndpoints.byId(id), {
    method: 'PUT',
    body: await buildMultimediaFormData(payload),
    headers: {}
  });
}

export function deleteMultimediaRequest(id) {
  return apiRequest(multimediaEndpoints.byId(id), {
    method: 'DELETE'
  });
}

export function downloadMultimediaRequest(id) {
  return apiRequest(multimediaEndpoints.download(id), {
    responseType: 'blob'
  });
}

export function downloadAudioWithCustomNameRequest(payload = {}) {
  return apiRequest(multimediaEndpoints.downloadAudio, {
    method: 'POST',
    body: {
      url: String(payload?.url || ''),
      language: String(payload?.language || ''),
      name: String(payload?.name || '')
    },
    responseType: 'blob'
  });
}
